import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  BookOpen,
  FileText,
  Award,
  GraduationCap,
  User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentGradesPage = () => {
  const { studentId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [student, setStudent] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Subject modal
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectName, setSubjectName] = useState('');
  const [savingSubject, setSavingSubject] = useState(false);
  
  // Entry modal
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entrySubjectId, setEntrySubjectId] = useState('');
  const [entryTitle, setEntryTitle] = useState('');
  const [entryScore, setEntryScore] = useState('');
  const [savingEntry, setSavingEntry] = useState(false);
  
  // Entries per subject
  const [entriesMap, setEntriesMap] = useState({});

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [studentRes, subjectsRes] = await Promise.all([
        axios.get(`${API}/grades/students/${studentId}`, { headers }),
        axios.get(`${API}/grades/subjects/${studentId}`, { headers })
      ]);
      
      setStudent(studentRes.data);
      setSubjects(subjectsRes.data);
      
      // Fetch entries for each subject
      const entriesPromises = subjectsRes.data.map(s =>
        axios.get(`${API}/grades/entries/${s.id}`, { headers })
      );
      const entriesResults = await Promise.all(entriesPromises);
      
      const newEntriesMap = {};
      subjectsRes.data.forEach((s, i) => {
        newEntriesMap[s.id] = entriesResults[i].data;
      });
      setEntriesMap(newEntriesMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load student data');
      navigate('/grades');
    } finally {
      setLoading(false);
    }
  };

  // Subject handlers
  const handleAddSubject = () => {
    setEditingSubject(null);
    setSubjectName('');
    setShowSubjectModal(true);
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectName(subject.subject_name);
    setShowSubjectModal(true);
  };

  const handleSaveSubject = async () => {
    if (!subjectName.trim()) {
      toast.error('Please enter a subject name');
      return;
    }

    setSavingSubject(true);
    try {
      const headers = getAuthHeader();
      
      if (editingSubject) {
        await axios.put(`${API}/grades/subjects/${editingSubject.id}`, 
          { subject_name: subjectName }, { headers });
        toast.success('Subject updated');
      } else {
        await axios.post(`${API}/grades/subjects`,
          { student_id: studentId, subject_name: subjectName }, { headers });
        toast.success('Subject added');
      }
      
      fetchData();
      setShowSubjectModal(false);
    } catch (error) {
      console.error('Error saving subject:', error);
      toast.error('Failed to save subject');
    } finally {
      setSavingSubject(false);
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!confirm('Delete this subject and all its grades?')) return;
    
    try {
      await axios.delete(`${API}/grades/subjects/${subjectId}`, {
        headers: getAuthHeader()
      });
      toast.success('Subject deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast.error('Failed to delete subject');
    }
  };

  // Entry handlers
  const handleAddEntry = (subjectId) => {
    setEditingEntry(null);
    setEntrySubjectId(subjectId);
    setEntryTitle('');
    setEntryScore('');
    setShowEntryModal(true);
  };

  const handleEditEntry = (entry, subjectId) => {
    setEditingEntry(entry);
    setEntrySubjectId(subjectId);
    setEntryTitle(entry.title);
    setEntryScore(entry.score.toString());
    setShowEntryModal(true);
  };

  const handleSaveEntry = async () => {
    if (!entryTitle.trim() || entryScore === '') {
      toast.error('Please fill in all fields');
      return;
    }

    const score = parseFloat(entryScore);
    if (isNaN(score) || score < 0 || score > 100) {
      toast.error('Score must be between 0 and 100');
      return;
    }

    setSavingEntry(true);
    try {
      const headers = getAuthHeader();
      
      if (editingEntry) {
        await axios.put(`${API}/grades/entries/${editingEntry.id}`,
          { title: entryTitle, score }, { headers });
        toast.success('Grade updated');
      } else {
        await axios.post(`${API}/grades/entries`,
          { subject_id: entrySubjectId, title: entryTitle, score }, { headers });
        toast.success('Grade added');
      }
      
      fetchData();
      setShowEntryModal(false);
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save grade');
    } finally {
      setSavingEntry(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Delete this grade entry?')) return;
    
    try {
      await axios.delete(`${API}/grades/entries/${entryId}`, {
        headers: getAuthHeader()
      });
      toast.success('Grade deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete grade');
    }
  };

  const getGradeColor = (letterGrade) => {
    if (letterGrade.startsWith('A')) return 'text-green-600 bg-green-50';
    if (letterGrade.startsWith('B')) return 'text-blue-600 bg-blue-50';
    if (letterGrade.startsWith('C')) return 'text-yellow-600 bg-yellow-50';
    if (letterGrade === 'D') return 'text-orange-600 bg-orange-50';
    if (letterGrade === 'F') return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-48 mb-4" />
          <Skeleton className="h-6 w-32 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <h1 className="text-2xl font-medium text-primary mb-4">Student not found</h1>
          <Button onClick={() => navigate('/grades')}>Back to Grades</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            className="gap-2 mb-4"
            onClick={() => navigate('/grades')}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Students
          </Button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="font-serif text-3xl font-medium text-primary">
                  {student.first_name} {student.last_name}
                </h1>
                <p className="text-muted-foreground">{student.grade_level}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Overall GPA</p>
                <p className="text-3xl font-bold text-primary">{student.overall_gpa.toFixed(2)}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => navigate(`/grades/${studentId}/report-card`)}
                  data-testid="view-report-card"
                >
                  <FileText className="w-4 h-4" />
                  Report Card
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => navigate(`/grades/${studentId}/transcript`)}
                  data-testid="view-transcript"
                >
                  <Award className="w-4 h-4" />
                  Transcript
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Add Subject Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Button onClick={handleAddSubject} className="gap-2" data-testid="add-subject-btn">
            <Plus className="w-4 h-4" />
            Add Subject
          </Button>
        </motion.div>

        {/* Subjects */}
        {subjects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl font-medium text-primary mb-2">No subjects yet</h3>
            <p className="text-muted-foreground mb-6">Add subjects to start tracking grades</p>
            <Button onClick={handleAddSubject} className="gap-2">
              <Plus className="w-4 h-4" />
              Add First Subject
            </Button>
          </motion.div>
        ) : (
          <Accordion type="multiple" defaultValue={subjects.map(s => s.id)} className="space-y-4">
            {subjects.map((subject, index) => (
              <motion.div
                key={subject.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <AccordionItem value={subject.id} className="border rounded-lg bg-card">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 text-primary" />
                        <span className="font-medium text-lg">{subject.subject_name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Average</p>
                          <p className="font-bold">{subject.average.toFixed(1)}%</p>
                        </div>
                        <Badge className={`${getGradeColor(subject.letter_grade)} font-bold`}>
                          {subject.letter_grade}
                        </Badge>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">GPA</p>
                          <p className="font-bold">{subject.gpa.toFixed(1)}</p>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => handleEditSubject(subject)}
                        >
                          <Edit2 className="w-3 h-3" />
                          Edit Subject
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteSubject(subject.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        className="gap-1"
                        onClick={() => handleAddEntry(subject.id)}
                        data-testid={`add-entry-${subject.id}`}
                      >
                        <Plus className="w-3 h-3" />
                        Add Grade
                      </Button>
                    </div>

                    {entriesMap[subject.id]?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Assignment</TableHead>
                            <TableHead className="text-right">Score</TableHead>
                            <TableHead className="w-24"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entriesMap[subject.id].map((entry) => (
                            <TableRow key={entry.id}>
                              <TableCell className="font-medium">{entry.title}</TableCell>
                              <TableCell className="text-right">{entry.score}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleEditEntry(entry, subject.id)}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteEntry(entry.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No grades yet. Click "Add Grade" to add the first one.
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        )}
      </main>

      {/* Subject Modal */}
      <Dialog open={showSubjectModal} onOpenChange={setShowSubjectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingSubject ? 'Edit Subject' : 'Add Subject'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="subject-name">Subject Name</Label>
            <Input
              id="subject-name"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              placeholder="e.g., Mathematics, Science, English"
              className="mt-2"
              onKeyDown={(e) => e.key === 'Enter' && handleSaveSubject()}
              data-testid="subject-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubjectModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSubject} disabled={savingSubject} data-testid="save-subject-btn">
              {savingSubject && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingSubject ? 'Save Changes' : 'Add Subject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry Modal */}
      <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingEntry ? 'Edit Grade' : 'Add Grade'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="entry-title">Assignment Name</Label>
              <Input
                id="entry-title"
                value={entryTitle}
                onChange={(e) => setEntryTitle(e.target.value)}
                placeholder="e.g., Unit 1 Test, Homework 3"
                className="mt-2"
                data-testid="entry-title-input"
              />
            </div>
            <div>
              <Label htmlFor="entry-score">Score (0-100)</Label>
              <Input
                id="entry-score"
                type="number"
                min="0"
                max="100"
                value={entryScore}
                onChange={(e) => setEntryScore(e.target.value)}
                placeholder="95"
                className="mt-2"
                data-testid="entry-score-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} disabled={savingEntry} data-testid="save-entry-btn">
              {savingEntry && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingEntry ? 'Save Changes' : 'Add Grade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentGradesPage;
